import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { API_BASE_URL, ApiService } from './api.service';
import { AvailabilityItem, BookingOut, MealTypeOut, RoomTypeOut, VerificationSendLog, View } from './models';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, FormsModule, DecimalPipe],
  template: `
    <div class="page">
      <header class="topbar">
        <h1 (click)="goHome()" style="cursor:pointer">🏨 線上訂房</h1>
        <nav>
          <button class="nav-btn" (click)="goVerificationLogs()">驗證碼發送紀錄</button>
          <button class="nav-btn" (click)="goHome()">首頁</button>
          <button class="nav-btn" (click)="goLookup()">查訂單</button>
          <button class="nav-btn" (click)="goCancel()">取消訂單</button>
          <button class="nav-btn" (click)="goRoomCreate()">新增房型</button>
        </nav>
      </header>

      <ng-container *ngIf="view === 'verification-logs'">
        <h2>驗證碼發送紀錄</h2>
        <p class="sub">測試用：每次訪客流程寄出驗證碼都會寫入 D1，這裡依新到舊列出明文驗證碼（沒有 SMTP 也能測）。</p>
        <p *ngIf="error" class="error">{{ error }}</p>
        <p *ngIf="loading" class="muted">載入中…</p>
        <div class="card" *ngIf="!loading && !error && verificationSendLogs.length === 0">
          <p class="muted" style="margin:0">目前還沒有發送紀錄。到「查訂單」或「取消訂單」寄一次驗證碼後再回來。</p>
        </div>
        <div class="card table-wrap" *ngIf="!loading && !error && verificationSendLogs.length > 0">
          <table class="log-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>驗證碼</th>
                <th>用途</th>
                <th>發送時間</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of verificationSendLogs">
                <td>{{ row.email }}</td>
                <td><span class="code">{{ row.code }}</span></td>
                <td>{{ row.purpose === 'cancel' ? '取消訂單' : '查詢訂單' }}</td>
                <td>{{ sentAt(row.created_at) | date:'yyyy-MM-dd HH:mm:ss' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </ng-container>

      <ng-container *ngIf="view === 'home'">
        <div class="panel">
          <form (ngSubmit)="search()">
            <label>入住日<input type="date" [(ngModel)]="checkIn" name="checkIn" [min]="today" required /></label>
            <label>退房日<input type="date" [(ngModel)]="checkOut" name="checkOut" [min]="checkIn" required /></label>
            <label class="btn-wrap"><button type="submit">查詢空房</button></label>
          </form>
        </div>
        <p *ngIf="error" class="error">{{ error }}</p>

        <h2>目前提供的房型</h2>
        <p *ngIf="loadingRooms" class="muted">載入中…</p>
        <div class="rooms">
          <div class="room" *ngFor="let r of roomTypes">
            <div class="room-thumb" *ngIf="r.image_list.length > 0">
              <img [src]="resolveImageUrl(r.image_list[0])" [alt]="r.name" loading="lazy" />
            </div>
            <div class="room-info">
              <h3>{{ r.name }}</h3>
              <p class="muted">可住 {{ r.capacity }} 人｜共 {{ r.total_rooms }} 間</p>
              <p>{{ r.description }}</p>
            </div>
            <div class="room-buy">
              <div class="price">NT$ {{ r.price_per_night | number:'1.0-0' }} <small>/ 晚</small></div>
              <button type="button" (click)="openRoomDetailFromHome(r)">選這間</button>
            </div>
          </div>
          <div class="card empty" *ngIf="!loadingRooms && roomTypes.length === 0">目前沒有可訂購的房型。</div>
        </div>
      </ng-container>

      <ng-container *ngIf="view === 'search'">
        <div class="panel">
          <form (ngSubmit)="search()">
            <label>入住日<input type="date" [(ngModel)]="checkIn" name="searchCheckIn" [min]="today" required /></label>
            <label>退房日<input type="date" [(ngModel)]="checkOut" name="searchCheckOut" [min]="checkIn" required /></label>
            <label class="btn-wrap"><button type="submit">重新查詢</button></label>
          </form>
        </div>
        <p class="sub" *ngIf="searchResult">{{ checkIn }} → {{ checkOut }}，共 {{ searchResult.nights }} 晚。</p>
        <p *ngIf="error" class="error">{{ error }}</p>
        <p *ngIf="loading" class="muted">查詢中…</p>

        <div class="rooms">
          <div class="room" *ngFor="let item of searchResult?.items ?? []">
            <div class="room-thumb" *ngIf="item.image_list.length > 0">
              <img [src]="resolveImageUrl(item.image_list[0])" [alt]="item.name" loading="lazy" />
            </div>
            <div class="room-info">
              <h3>{{ item.name }}</h3>
              <p class="muted">可住 {{ item.capacity }} 人</p>
              <p>{{ item.description }}</p>
              <p style="margin-top:0.4rem">
                <span class="tag ok" *ngIf="item.available_rooms > 2">尚有 {{ item.available_rooms }} 間</span>
                <span class="tag warn" *ngIf="item.available_rooms > 0 && item.available_rooms <= 2">僅剩 {{ item.available_rooms }} 間</span>
                <span class="tag err" *ngIf="item.available_rooms === 0">已訂滿</span>
              </p>
            </div>
            <div class="room-buy">
              <div class="price">NT$ {{ item.room_total_price | number:'1.0-0' }}</div>
              <div class="muted" style="font-size:0.8rem">{{ item.nights }} 晚 × NT$ {{ item.price_per_night | number:'1.0-0' }}</div>
              <button *ngIf="item.available_rooms > 0" (click)="openRoomDetail(item)">選這間</button>
              <button *ngIf="item.available_rooms === 0" disabled>無法訂購</button>
            </div>
          </div>
          <div class="card empty" *ngIf="!loading && searchResult && searchResult.items.length === 0">
            這段期間沒有符合條件的房型，換個日期試試。
          </div>
        </div>
      </ng-container>

      <ng-container *ngIf="view === 'room-detail' && selectedRoomDetail">
        <h2>房型明細</h2>
        <p class="sub">在這裡填寫人數與餐點（同一間只能選一種餐）。</p>
        <p *ngIf="error" class="error">{{ error }}</p>

        <div class="card">
          <div class="gallery" *ngIf="selectedRoomDetail.image_list.length > 0">
            <img *ngFor="let src of selectedRoomDetail.image_list" [src]="resolveImageUrl(src)" [alt]="selectedRoomDetail.name" loading="lazy" />
          </div>
          <dl class="summary">
            <dt>房型</dt><dd>{{ selectedRoomDetail.name }}（每間可住 {{ selectedRoomDetail.capacity }} 人）</dd>
            <dt *ngIf="selectedRoomDetail.guest_summary">Guest</dt><dd *ngIf="selectedRoomDetail.guest_summary">{{ selectedRoomDetail.guest_summary }}</dd>
            <dt *ngIf="selectedRoomDetail.description">介紹</dt><dd *ngIf="selectedRoomDetail.description">{{ selectedRoomDetail.description }}</dd>
            <dt>Check In / Out</dt><dd>{{ selectedRoomDetail.check_in_time }} - {{ selectedRoomDetail.check_out_time }}</dd>
            <dt *ngIf="selectedRoomDetail.beds">Beds</dt><dd *ngIf="selectedRoomDetail.beds">{{ selectedRoomDetail.beds }}</dd>
            <dt *ngIf="selectedRoomDetail.wifi">Wi-Fi</dt><dd *ngIf="selectedRoomDetail.wifi">{{ selectedRoomDetail.wifi }}</dd>
            <dt *ngIf="selectedRoomDetail.smoking">Smoking</dt><dd *ngIf="selectedRoomDetail.smoking">{{ selectedRoomDetail.smoking }}</dd>
            <dt>入住 / 退房</dt><dd>{{ checkIn }} → {{ checkOut }}（{{ selectedRoomDetail.nights }} 晚）</dd>
            <dt>剩餘房數</dt><dd>{{ selectedRoomDetail.available_rooms }} 間</dd>
            <dt>住宿人數</dt><dd>成人 {{ bookingForm.adults }}｜學童 {{ bookingForm.children }}（計 {{ occupancy() }} 人）</dd>
            <dt>餐點方案</dt><dd>{{ selectedMealLabel() }}</dd>
            <dt>總價</dt><dd>NT$ {{ detailTotal() | number:'1.0-0' }}</dd>
          </dl>
        </div>

        <div class="card" style="margin-top:1rem">
          <div class="form-grid">
            <label>成人
              <input type="number" [(ngModel)]="bookingForm.adults" name="detailAdults" min="1" [max]="selectedRoomDetail.capacity" (change)="refreshRoomDetailQuote()" required />
            </label>
            <label>學童（算一人）
              <input type="number" [(ngModel)]="bookingForm.children" name="detailChildren" min="0" [max]="selectedRoomDetail.capacity" (change)="refreshRoomDetailQuote()" />
                          </label>
            <label>餐點方案
              <select [(ngModel)]="bookingForm.meal_type_id" name="detailMealType" (change)="refreshRoomDetailQuote()">
                <option [ngValue]="null">不加購</option>
                <option *ngFor="let m of mealTypes" [ngValue]="m.id">{{ m.name }}{{ m.includes ? '（' + m.includes + '）' : '' }}</option>
              </select>
            </label>
            <label>訂房人姓名
              <input type="text" [(ngModel)]="bookingForm.guest_name" name="detailGuestName" maxlength="100" required />
            </label>
            <label>Email
              <input type="email" [(ngModel)]="bookingForm.guest_email" name="detailGuestEmail" maxlength="200" required />
            </label>
            <label>電話（選填）
              <input type="tel" [(ngModel)]="bookingForm.guest_phone" name="detailGuestPhone" maxlength="50" />
            </label>
          </div>
          <label style="display:grid;gap:6px;font-size:14px;margin-top:0.9rem">備註（選填）
            <textarea [(ngModel)]="bookingForm.notes" name="detailNotes" rows="3" maxlength="500"></textarea>
          </label>
          <div class="action-row">
            <button (click)="submitBooking()" [disabled]="submitting || loading || selectedRoomDetail.available_rooms === 0">
              {{ submitting ? '送出中…' : '確認訂房' }}
            </button>
            <button class="secondary" (click)="view = 'search'">回搜尋結果</button>
          </div>
        </div>
      </ng-container>

      <ng-container *ngIf="view === 'booking-detail' && currentBooking">
        <h2>訂單 <span class="code">{{ currentBooking.code }}</span></h2>
        <p class="sub">請保留這組編號，之後可以在「查訂單」頁面查詢或取消。</p>

        <div class="card">
          <dl class="summary">
            <dt>狀態</dt>
            <dd>
              <span class="tag err" *ngIf="currentBooking.status === 'cancelled'">已取消</span>
              <span class="tag warn" *ngIf="currentBooking.status === 'pending'">待確認</span>
              <span class="tag ok" *ngIf="currentBooking.status === 'confirmed'">已確認</span>
            </dd>
            <dt>房型</dt><dd>{{ currentBooking.room_type_name }}</dd>
            <dt>入住 / 退房</dt><dd>{{ currentBooking.check_in }} → {{ currentBooking.check_out }}（{{ currentBooking.nights }} 晚）</dd>
            <dt>房間數 / 人數</dt><dd>{{ currentBooking.rooms }} 間 / {{ currentBooking.guests }} 人</dd>
            <dt>住宿人數</dt><dd>成人 {{ currentBooking.adults }}｜學童 {{ currentBooking.children }}</dd>
            <dt>餐點方案</dt><dd>{{ currentBooking.meal_plan_label }}</dd>
            <dt>總金額</dt><dd>NT$ {{ currentBooking.total_price | number:'1.0-0' }}</dd>
            <dt>訂房人</dt><dd>{{ currentBooking.guest_name }}</dd>
            <dt>Email</dt><dd>{{ currentBooking.guest_email }}</dd>
            <dt *ngIf="currentBooking.guest_phone">電話</dt><dd *ngIf="currentBooking.guest_phone">{{ currentBooking.guest_phone }}</dd>
            <dt *ngIf="currentBooking.notes">備註</dt><dd *ngIf="currentBooking.notes">{{ currentBooking.notes }}</dd>
            <dt>訂單日期</dt><dd>{{ orderedAt(currentBooking) | date:'yyyy-MM-dd HH:mm' }}</dd>
          </dl>

          <p *ngIf="error" class="error">{{ error }}</p>

          <div class="action-row" *ngIf="currentBooking.status !== 'cancelled'">
            <button class="danger" (click)="cancelBooking()" [disabled]="submitting">
              {{ submitting ? '取消中…' : '取消這筆訂單' }}
            </button>
          </div>
        </div>

        <button class="secondary" style="margin-top:1rem" (click)="goHome()">再訂一間</button>
      </ng-container>

      <ng-container *ngIf="view === 'lookup'">
        <h2>查詢訂單</h2>
        <p class="sub">先用訂房時填的 Email 收驗證碼，通過驗證後就會列出這個 Email 的所有訂單。</p>

        <div class="panel auth-card" *ngIf="lookupStep === 'email'">
          <form (ngSubmit)="sendLookupCode()">
            <label>Email
              <input type="email" [(ngModel)]="lookupEmail" name="lookupEmail" placeholder="訂房時填的 Email" maxlength="200" required />
            </label>
            <label class="btn-wrap">
              <button type="submit" [disabled]="submitting">{{ submitting ? '寄送中…' : '寄送驗證碼' }}</button>
            </label>
          </form>
        </div>

        <div class="panel auth-card" *ngIf="lookupStep === 'code'">
          <p *ngIf="lookupNotice" class="notice">{{ lookupNotice }}</p>
          <p *ngIf="lookupDebugCode" class="notice">
            （POC 顯示）驗證碼：<span class="code">{{ lookupDebugCode }}</span>
            — 這是 demo 用的捷徑，正式環境只會寄信、不會顯示在畫面上。
          </p>
          <form (ngSubmit)="submitLookupCode()">
            <label>驗證碼（6 位數）
              <input type="text" [(ngModel)]="lookupVerifyCode" name="lookupVerifyCode" inputmode="numeric" maxlength="6" placeholder="123456" required />
            </label>
            <label class="btn-wrap">
              <button type="submit" [disabled]="submitting">{{ submitting ? '驗證中…' : '驗證' }}</button>
            </label>
          </form>
          <div class="action-row">
            <button class="secondary" (click)="sendLookupCode()" [disabled]="submitting || lookupResendSeconds > 0">
              {{ lookupResendSeconds > 0 ? '重新寄送（' + lookupResendSeconds + ' 秒）' : '重新寄送' }}
            </button>
            <button class="secondary" (click)="resetLookup()">換一個 Email</button>
          </div>
        </div>

        <div class="panel auth-card" *ngIf="lookupStep === 'list'">
          <div class="action-row" style="margin-top:0; align-items:center">
            <span class="muted">已驗證：{{ verifiedEmail() }}</span>
            <button class="secondary" (click)="logoutGuest()">登出</button>
          </div>
        </div>

        <p *ngIf="error" class="error">{{ error }}</p>
        <p *ngIf="loading" class="muted">載入中…</p>

        <div class="rooms" style="margin-top:12px" *ngIf="lookupStep === 'list'">
          <div class="room booking-row" *ngFor="let b of bookingList" (click)="openBookingDetail(b)">
            <div class="room-info">
              <h3>
                <span class="code">{{ b.code }}</span>
                <span class="tag err" *ngIf="b.status === 'cancelled'">已取消</span>
                <span class="tag warn" *ngIf="b.status === 'pending'">待確認</span>
                <span class="tag ok" *ngIf="b.status === 'confirmed'">已確認</span>
              </h3>
              <p class="muted">訂單日期：{{ orderedAt(b) | date:'yyyy-MM-dd HH:mm' }}</p>
              <p class="muted">{{ b.room_type_name }}｜{{ b.check_in }} → {{ b.check_out }}（{{ b.nights }} 晚）</p>
              <p class="muted">{{ b.guest_name }}｜{{ b.guests }} 人｜{{ b.meal_plan_label }}</p>
            </div>
            <div class="room-buy">
              <div class="price">NT$ {{ b.total_price | number:'1.0-0' }}</div>
              <button type="button" (click)="$event.stopPropagation(); openBookingDetail(b)">查看</button>
            </div>
          </div>
          <div class="card empty" *ngIf="!loading && bookingList.length === 0">
            這個 Email 目前沒有任何訂單。
          </div>
        </div>
      </ng-container>

      <ng-container *ngIf="view === 'cancel'">
        <h2>取消訂單</h2>
        <p class="sub">不必先登入。填訂單編號與訂購 Email，收到驗證碼後即可取消。</p>

        <div class="panel">
          <form (ngSubmit)="sendCancelCode()">
            <label>訂單編號
              <input type="text" [(ngModel)]="cancelCode" name="cancelCode" placeholder="例如 A3F9K2" maxlength="12" required />
            </label>
            <label>訂購人 Email
              <input type="email" [(ngModel)]="cancelEmail" name="cancelEmail" placeholder="訂房時填的 Email" maxlength="200" required />
            </label>
            <label class="btn-wrap">
              <button type="submit" [disabled]="submitting || cancelResendSeconds > 0">
                {{ cancelResendSeconds > 0 ? '重新寄送（' + cancelResendSeconds + ' 秒）' : (cancelStep === 'code' ? '重新寄送' : '寄送驗證碼') }}
              </button>
            </label>
          </form>
        </div>

        <div class="card" style="margin-top:1rem" *ngIf="cancelStep === 'code'">
          <p *ngIf="cancelNotice" class="notice">{{ cancelNotice }}</p>
          <p *ngIf="cancelDebugCode" class="notice">
            （POC 顯示）驗證碼：<span class="code">{{ cancelDebugCode }}</span>
            — 這是 demo 用的捷徑，正式環境只會寄信、不會顯示在畫面上。
          </p>
          <div class="form-grid">
            <label>驗證碼（6 位數）
              <input type="text" [(ngModel)]="cancelVerifyCode" name="cancelVerifyCode" inputmode="numeric" maxlength="6" placeholder="123456" required />
            </label>
          </div>
          <div class="action-row">
            <button class="danger" (click)="confirmCancelWithCode()" [disabled]="submitting">
              {{ submitting ? '取消中…' : '確認取消訂單' }}
            </button>
            <button class="secondary" (click)="goHome()">回首頁</button>
          </div>
        </div>

        <p *ngIf="error" class="error">{{ error }}</p>
      </ng-container>

      <ng-container *ngIf="view === 'room-create'">
        <h2>新增房型</h2>
        <p class="sub">建立後會出現在首頁，若勾選「開放訂購」就可立即被查到。</p>

        <div class="card">
          <div class="form-grid">
            <label>房型名稱
              <input type="text" [(ngModel)]="newRoom.name" name="newRoomName" maxlength="100" required />
            </label>
            <label>每間人數上限
              <input type="number" [(ngModel)]="newRoom.capacity" name="newRoomCapacity" min="1" max="20" required />
            </label>
            <label>每晚價格
              <input type="number" [(ngModel)]="newRoom.price_per_night" name="newRoomPrice" min="0" required />
            </label>
            <label>房間數
              <input type="number" [(ngModel)]="newRoom.total_rooms" name="newRoomTotal" min="0" max="999" required />
            </label>
          </div>
          <label style="margin-top:0.9rem">房型介紹
            <textarea [(ngModel)]="newRoom.description" name="newRoomDescription" rows="3" maxlength="2000"></textarea>
          </label>
          <div class="form-grid" style="margin-top:0.9rem">
            <label>Guest
              <input type="text" [(ngModel)]="newRoom.guest_summary" name="newRoomGuestSummary" maxlength="100" placeholder="4 adults, 1 child" />
            </label>
            <label>Check In
              <input type="text" [(ngModel)]="newRoom.check_in_time" name="newRoomCheckIn" maxlength="10" />
            </label>
            <label>Check Out
              <input type="text" [(ngModel)]="newRoom.check_out_time" name="newRoomCheckOut" maxlength="10" />
            </label>
            <label>Beds
              <input type="text" [(ngModel)]="newRoom.beds" name="newRoomBeds" maxlength="100" placeholder="2 bunk beds" />
            </label>
            <label>Wi-Fi
              <input type="text" [(ngModel)]="newRoom.wifi" name="newRoomWifi" maxlength="100" />
            </label>
            <label>Smoking
              <input type="text" [(ngModel)]="newRoom.smoking" name="newRoomSmoking" maxlength="100" />
            </label>
          </div>
          <label style="margin-top:0.9rem">圖片網址（一行一個）
            <textarea [(ngModel)]="newRoom.imageLines" name="newRoomImages" rows="3" maxlength="5000"></textarea>
          </label>
          <label style="margin-top:0.9rem; display:flex; align-items:center; gap:0.5rem;">
            <input type="checkbox" [(ngModel)]="newRoom.is_active" name="newRoomActive" style="width:auto;" />
            開放訂購
          </label>

          <p *ngIf="roomNotice" class="notice">{{ roomNotice }}</p>
          <p *ngIf="error" class="error">{{ error }}</p>

          <div class="action-row">
            <button (click)="createRoomType()" [disabled]="submitting">{{ submitting ? '新增中…' : '新增房型' }}</button>
          </div>
        </div>
      </ng-container>
    </div>
  `,
})
export class AppComponent implements OnInit, OnDestroy {
  view: View = 'home';

  today = this.formatDate(0);
  checkIn = this.formatDate(1);
  checkOut = this.formatDate(2);

  roomTypes: RoomTypeOut[] = [];
  mealTypes: MealTypeOut[] = [];
  loadingRooms = false;

  searchResult: { nights: number; items: AvailabilityItem[] } | null = null;
  loading = false;
  error = '';

  selectedRoomDetail: AvailabilityItem | null = null;
  bookingForm = {
    adults: 1,
    children: 0,
    meal_type_id: null as number | null,
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    notes: '',
  };
  submitting = false;

  currentBooking: BookingOut | null = null;
  bookingList: BookingOut[] = [];

  // 查訂單：email（填 Email）→ code（輸入驗證碼）→ list（已驗證，顯示訂單）
  lookupStep: 'email' | 'code' | 'list' = 'email';
  lookupEmail = '';
  lookupVerifyCode = '';
  lookupNotice = '';
  lookupDebugCode = '';
  lookupResendSeconds = 0;

  verificationSendLogs: VerificationSendLog[] = [];

  // 取消訂單（免登入）：request（填編號與 Email）→ code（輸入驗證碼後取消）
  cancelStep: 'request' | 'code' = 'request';
  cancelCode = '';
  cancelEmail = '';
  cancelVerifyCode = '';
  cancelNotice = '';
  cancelDebugCode = '';
  cancelResendSeconds = 0;

  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  roomNotice = '';
  newRoom = {
    name: '',
    capacity: 2,
    price_per_night: 2000,
    total_rooms: 1,
    description: '',
    guest_summary: '',
    check_in_time: '15:00',
    check_out_time: '11:00',
    beds: '',
    wifi: 'Free Wi-Fi',
    smoking: 'Non-smoking',
    imageLines: '',
    is_active: true,
  };

  constructor(private readonly api: ApiService) {}

  ngOnInit(): void {
    this.loadRoomTypes();
    this.loadMealTypes();
  }

  ngOnDestroy(): void {
    this.stopCountdown();
  }

  goHome(): void {
    this.view = 'home';
    this.error = '';
  }

  goVerificationLogs(): void {
    this.view = 'verification-logs';
    this.error = '';
    this.loading = true;
    this.verificationSendLogs = [];
    this.api.listVerificationSendLogs().subscribe({
      next: (rows) => {
        if (!Array.isArray(rows)) {
          this.verificationSendLogs = [];
          this.error = '載入發送紀錄失敗：回應格式不正確';
          this.loading = false;
          return;
        }
        this.verificationSendLogs = this.sortSendLogsNewestFirst(rows);
        this.loading = false;
      },
      error: (err) => {
        this.verificationSendLogs = [];
        this.error = this.httpErrorDetail(err, '載入發送紀錄失敗');
        this.loading = false;
      },
    });
  }

  goLookup(): void {
    this.view = 'lookup';
    this.error = '';
    this.lookupVerifyCode = '';
    this.lookupNotice = '';
    this.lookupDebugCode = '';
    if (this.api.hasGuestToken()) {
      this.lookupEmail = this.api.guestEmail;
      this.lookupStep = 'list';
      this.loadMyBookings();
      return;
    }
    this.lookupStep = 'email';
    this.bookingList = [];
  }

  goCancel(bookingCode = '', email = ''): void {
    this.view = 'cancel';
    this.error = '';
    this.cancelStep = 'request';
    this.cancelCode = bookingCode;
    this.cancelEmail = email;
    this.cancelVerifyCode = '';
    this.cancelNotice = '';
    this.cancelDebugCode = '';
  }

  goRoomCreate(): void {
    this.view = 'room-create';
    this.error = '';
    this.roomNotice = '';
  }

  search(): void {
    this.error = '';
    this.loading = true;
    this.searchResult = null;
    this.view = 'search';
    this.api.searchAvailability(this.checkIn, this.checkOut, null, null).subscribe({
      next: (res) => {
        this.searchResult = res;
        this.loading = false;
      },
      error: (err) => {
        this.error = err?.error?.detail ?? '查詢失敗';
        this.loading = false;
      },
    });
  }

  openRoomDetailFromHome(room: RoomTypeOut): void {
    this.error = '';
    this.loading = true;
    this.api.searchAvailability(this.checkIn, this.checkOut, null, null, room.id).subscribe({
      next: (res) => {
        this.loading = false;
        if (res.items.length === 0) {
          this.error = '這個房型在目前條件下沒有空房，請調整日期。';
          return;
        }
        this.openRoomDetail(res.items[0]);
      },
      error: (err) => {
        this.error = err?.error?.detail ?? '查詢失敗';
        this.loading = false;
      },
    });
  }

  openRoomDetail(room: AvailabilityItem): void {
    this.selectedRoomDetail = room;
    this.resetBookingForm();
    this.error = '';
    this.view = 'room-detail';
    this.refreshRoomDetailQuote();
  }

  resetBookingForm(): void {
    this.bookingForm = {
      adults: 1,
      children: 0,
      meal_type_id: null,
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      notes: '',
    };
  }

  occupancy(): number {
    return Math.max(Number(this.bookingForm.adults) || 0, 1) + (Number(this.bookingForm.children) || 0);
  }

  refreshRoomDetailQuote(): void {
    if (!this.selectedRoomDetail) return;
    const adults = Math.max(Number(this.bookingForm.adults) || 0, 1);
    let children = Math.max(Number(this.bookingForm.children) || 0, 0);
    // 成人 + 學童（= 住宿人數）不可超過房型上限；幼兒不計。
    const capacity = this.selectedRoomDetail.capacity;
    if (adults + children > capacity) {
      children = Math.max(capacity - adults, 0);
    }
    this.bookingForm.adults = adults;
    this.bookingForm.children = children;
    const guests = adults + children;
    this.loading = true;
    this.api.searchAvailability(
      this.checkIn,
      this.checkOut,
      guests,
      this.bookingForm.meal_type_id,
      this.selectedRoomDetail.room_type_id,
    ).subscribe({
      next: (res) => {
        this.loading = false;
        if (res.items.length === 0) {
          this.error = '目前這個條件下沒有可訂房型，請調整人數或餐點。';
          this.view = 'search';
          this.selectedRoomDetail = null;
          return;
        }
        this.selectedRoomDetail = res.items[0];
      },
      error: (err) => {
        this.error = err?.error?.detail ?? '查詢明細失敗';
        this.loading = false;
      },
    });
  }

  detailTotal(): number {
    if (!this.selectedRoomDetail) return 0;
    return Number(this.selectedRoomDetail.total_price);
  }

  submitBooking(): void {
    if (!this.selectedRoomDetail) return;
    if (!this.bookingForm.guest_name || !this.bookingForm.guest_email) {
      this.error = '請填寫姓名與 Email';
      return;
    }
    this.submitting = true;
    this.error = '';
    this.api.createBooking({
      room_type_id: this.selectedRoomDetail.room_type_id,
      check_in: this.checkIn,
      check_out: this.checkOut,
      guest_name: this.bookingForm.guest_name,
      guest_email: this.bookingForm.guest_email,
      guest_phone: this.bookingForm.guest_phone,
      rooms: 1,
      adults: Math.max(Number(this.bookingForm.adults) || 0, 1),
      children: Math.max(Number(this.bookingForm.children) || 0, 0),
      meal_type_id: this.bookingForm.meal_type_id,
      notes: this.bookingForm.notes,
    }).subscribe({
      next: (booking) => {
        this.currentBooking = booking;
        this.submitting = false;
        this.view = 'booking-detail';
        // 訂單一送出去就把表單清空：訂房人姓名、Email、電話、備註都是個資，
        // 不該留在畫面上等下一個人接手用同一台裝置訂房。
        this.resetBookingForm();
        this.selectedRoomDetail = null;
      },
      error: (err) => {
        // 失敗時保留表單，使用者才不用重打一次。
        this.error = err?.error?.detail ?? '訂房失敗，請稍後再試';
        this.submitting = false;
      },
    });
  }

  verifiedEmail(): string {
    return this.api.guestEmail;
  }

  sendLookupCode(): void {
    const email = this.lookupEmail.trim();
    if (!email) {
      this.error = '請填寫 Email。';
      return;
    }
    this.submitting = true;
    this.error = '';
    this.lookupNotice = '';
    this.lookupDebugCode = '';
    this.api.requestVerificationCode(email, 'lookup').subscribe({
      next: (res) => {
        this.submitting = false;
        this.lookupStep = 'code';
        this.lookupNotice = `驗證碼已寄到 ${res.email}，${res.expires_in_minutes} 分鐘內有效。`;
        this.lookupDebugCode = res.debug_code ?? '';
        this.lookupResendSeconds = res.resend_after_seconds;
        this.startCountdown();
      },
      error: (err) => {
        this.error = err?.error?.detail ?? '寄送驗證碼失敗，請稍後再試';
        this.submitting = false;
      },
    });
  }

  submitLookupCode(): void {
    const code = this.lookupVerifyCode.trim();
    if (!code) {
      this.error = '請輸入驗證碼。';
      return;
    }
    this.submitting = true;
    this.error = '';
    this.api.verifyCode(this.lookupEmail.trim(), code, 'lookup').subscribe({
      next: () => {
        this.submitting = false;
        this.lookupVerifyCode = '';
        this.lookupNotice = '';
        this.lookupDebugCode = '';
        this.lookupResendSeconds = 0;
        this.lookupStep = 'list';
        this.loadMyBookings();
      },
      error: (err) => {
        this.error = err?.error?.detail ?? '驗證失敗，請確認驗證碼是否正確或已過期';
        this.submitting = false;
      },
    });
  }

  resetLookup(): void {
    this.lookupStep = 'email';
    this.lookupVerifyCode = '';
    this.lookupNotice = '';
    this.lookupDebugCode = '';
    this.lookupResendSeconds = 0;
    this.error = '';
  }

  logoutGuest(): void {
    this.api.clearGuestToken();
    this.bookingList = [];
    this.lookupEmail = '';
    this.resetLookup();
  }

  sendCancelCode(): void {
    const bookingCode = this.cancelCode.trim();
    const email = this.cancelEmail.trim();
    if (!bookingCode || !email) {
      this.error = '請填寫訂單編號與 Email。';
      return;
    }
    this.submitting = true;
    this.error = '';
    this.cancelNotice = '';
    this.cancelDebugCode = '';
    this.api.requestVerificationCode(email, 'cancel').subscribe({
      next: (res) => {
        this.submitting = false;
        this.cancelStep = 'code';
        this.cancelNotice = `驗證碼已寄到 ${res.email}，${res.expires_in_minutes} 分鐘內有效。`;
        this.cancelDebugCode = res.debug_code ?? '';
        this.cancelResendSeconds = res.resend_after_seconds;
        this.startCountdown();
      },
      error: (err) => {
        this.error = err?.error?.detail ?? '寄送驗證碼失敗，請稍後再試';
        this.submitting = false;
      },
    });
  }

  confirmCancelWithCode(): void {
    const bookingCode = this.cancelCode.trim();
    const verificationCode = this.cancelVerifyCode.trim();
    if (!verificationCode) {
      this.error = '請輸入驗證碼。';
      return;
    }
    if (!confirm(`確定要取消訂單 ${bookingCode} 嗎？取消後無法復原。`)) return;
    this.submitting = true;
    this.error = '';
    this.api.cancelBookingWithVerification(bookingCode, this.cancelEmail.trim(), verificationCode).subscribe({
      next: (booking) => {
        this.submitting = false;
        this.cancelVerifyCode = '';
        this.cancelNotice = '';
        this.cancelDebugCode = '';
        this.cancelResendSeconds = 0;
        this.openBookingDetail(booking);
      },
      error: (err) => {
        this.error = err?.error?.detail ?? '取消失敗，請稍後再試';
        this.submitting = false;
      },
    });
  }

  private loadMyBookings(): void {
    this.loading = true;
    this.error = '';
    this.api.listMyBookings().subscribe({
      next: (list) => {
        this.bookingList = list;
        this.loading = false;
      },
      error: (err) => {
        this.error = err?.error?.detail ?? '查詢失敗';
        this.loading = false;
        if (err?.status === 401) {
          // token 過期或被清掉，退回輸入 Email 的第一步重新驗證。
          this.api.clearGuestToken();
          this.bookingList = [];
          this.lookupStep = 'email';
        }
      },
    });
  }

  // 兩個頁面共用一顆計時器，倒數歸零就自己停掉。
  private startCountdown(): void {
    if (this.countdownTimer !== null) return;
    this.countdownTimer = setInterval(() => {
      if (this.lookupResendSeconds > 0) this.lookupResendSeconds -= 1;
      if (this.cancelResendSeconds > 0) this.cancelResendSeconds -= 1;
      if (this.lookupResendSeconds <= 0 && this.cancelResendSeconds <= 0) {
        this.stopCountdown();
      }
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  openBookingDetail(booking: BookingOut): void {
    this.currentBooking = booking;
    this.error = '';
    this.view = 'booking-detail';
  }

  cancelBooking(): void {
    if (!this.currentBooking) return;
    // 沒有通過驗證就不能直接取消，帶著這筆訂單的資料到「取消訂單」頁去收驗證碼。
    if (!this.api.hasGuestToken()) {
      this.goCancel(this.currentBooking.code, this.currentBooking.guest_email);
      this.error = '取消訂單需要 Email 驗證，請先寄送驗證碼。';
      return;
    }
    if (!confirm(`確定要取消訂單 ${this.currentBooking.code} 嗎？取消後無法復原。`)) return;
    this.submitting = true;
    this.error = '';
    this.api.cancelBookingWithVerification(this.currentBooking.code, this.currentBooking.guest_email, null).subscribe({
      next: (booking) => {
        this.currentBooking = booking;
        this.submitting = false;
      },
      error: (err) => {
        this.error = err?.error?.detail ?? '取消失敗，請稍後再試';
        this.submitting = false;
      },
    });
  }

  createRoomType(): void {
    if (!this.newRoom.name.trim()) {
      this.error = '請填寫房型名稱。';
      return;
    }
    this.submitting = true;
    this.error = '';
    this.roomNotice = '';

    const imageList = this.newRoom.imageLines
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    this.api.createRoomType(
      {
        name: this.newRoom.name.trim(),
        description: this.newRoom.description.trim(),
        image_list: imageList,
        capacity: this.newRoom.capacity,
        guest_summary: this.newRoom.guest_summary.trim(),
        check_in_time: this.newRoom.check_in_time.trim() || '15:00',
        check_out_time: this.newRoom.check_out_time.trim() || '11:00',
        beds: this.newRoom.beds.trim(),
        wifi: this.newRoom.wifi.trim() || 'Free Wi-Fi',
        smoking: this.newRoom.smoking.trim() || 'Non-smoking',
        price_per_night: this.newRoom.price_per_night,
        total_rooms: this.newRoom.total_rooms,
        is_active: this.newRoom.is_active,
      },
    ).subscribe({
      next: (created) => {
        this.submitting = false;
        this.roomNotice = `已新增房型「${created.name}」。`;
        this.newRoom = {
          name: '',
          capacity: 2,
          price_per_night: 2000,
          total_rooms: 1,
          description: '',
          guest_summary: '',
          check_in_time: '15:00',
          check_out_time: '11:00',
          beds: '',
          wifi: 'Free Wi-Fi',
          smoking: 'Non-smoking',
          imageLines: '',
          is_active: true,
        };
        this.loadRoomTypes();
      },
      error: (err) => {
        this.error = err?.error?.detail ?? '新增房型失敗，請稍後再試';
        this.submitting = false;
      },
    });
  }

  private loadRoomTypes(): void {
    this.loadingRooms = true;
    this.api.listPublicRoomTypes().subscribe({
      next: (types) => {
        this.roomTypes = types;
        this.loadingRooms = false;
      },
      error: () => {
        this.loadingRooms = false;
      },
    });
  }

  private loadMealTypes(): void {
    this.api.listPublicMealTypes().subscribe({
      next: (types) => {
        this.mealTypes = types;
      },
      error: () => {
        this.mealTypes = [];
      },
    });
  }

  selectedMealLabel(): string {
    const id = this.bookingForm.meal_type_id;
    if (id === null) return '不加購餐點';
    const meal = this.mealTypes.find((m) => m.id === id);
    return meal ? meal.name : '不加購餐點';
  }

  roomOnlyTotal(room: AvailabilityItem): number {
    return Number(room.room_total_price);
  }

  orderedAt(booking: BookingOut): Date {
    return this.sentAt(booking.created_at);
  }

  private sortSendLogsNewestFirst(rows: VerificationSendLog[]): VerificationSendLog[] {
    return [...rows].sort((left, right) => {
      const byTime = this.sentAt(right.created_at).getTime() - this.sentAt(left.created_at).getTime();
      if (byTime !== 0) return byTime;
      return (right.id ?? 0) - (left.id ?? 0);
    });
  }

  private httpErrorDetail(err: { error?: { detail?: unknown }; status?: number }, fallback: string): string {
    const detail = err?.error?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
    if (err?.status === 404) {
      return '載入發送紀錄失敗（Not Found）';
    }
    return fallback;
  }

  sentAt(raw: string): Date {
    // 後端的 created_at 是 UTC 但沒有帶時區標記（如 2026-09-09T01:52:02），
    // 直接交給 DatePipe 會被當成本地時間，在 UTC+8 就整整差 8 小時。
    const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(raw);
    return new Date(hasZone ? raw : `${raw}Z`);
  }

  resolveImageUrl(src: string): string {
    const value = src?.trim() ?? '';
    if (!value) return '';
    if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('//')) {
      return value;
    }
    if (value.startsWith('data:') || value.startsWith('blob:')) {
      return value;
    }
    return `${API_BASE_URL}${value.startsWith('/') ? '' : '/'}${value}`;
  }

  private formatDate(offsetDay: number): string {
    const d = new Date();
    d.setDate(d.getDate() + offsetDay);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
